import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { unwrapPaginated } from "@/lib/pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogScrollBody } from "@/components/ui/dialog";
import { Plus, Layers, Trash2, Pencil, Users } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

const emptyForm = () => ({
  name: "",
  audience_id: "",
  tags: "",
  match_mode: "any",
  description: "",
});

export default function SegmentsPage() {
  const confirm = useConfirm();
  const [audiences, setAudiences] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const fetcher = useCallback((page, limit) => api.get("/segments", { params: { page, limit } }), []);

  const {
    items: segments,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    reload,
  } = usePaginatedList(fetcher, []);

  useEffect(() => {
    api.get("/audiences").then((r) => setAudiences(unwrapPaginated(r.data).items));
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), audience_id: audiences[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      audience_id: s.audience_id,
      tags: (s.tags || []).join(", "),
      match_mode: s.match_mode || "any",
      description: s.description || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.audience_id) return toast.error("Name and audience are required");
    const payload = {
      name: form.name.trim(),
      audience_id: form.audience_id,
      match_mode: form.match_mode,
      description: form.description,
      tags: form.tags.split(/[,|]/).map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.put(`/segments/${editingId}`, payload);
        toast.success("Segment updated");
      } else {
        await api.post("/segments", payload);
        toast.success("Segment created");
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete segment?",
      description: "Delete this segment? Saved filters will be removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/segments/${id}`);
    toast.success("Segment removed");
    reload();
  };

  return (
    <div data-testid="segments-page">
      <PageHeader
        title="Segments"
        subtitle="Saved audience slices by tags — use them when targeting campaigns."
        action={
          <Button onClick={openCreate} className="rounded-sm h-11 btn-brand font-semibold" data-testid="add-segment-button">
            <Plus className="h-4 w-4 mr-2" /> New segment
          </Button>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
            <Layers className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">Create your first segment</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-md mx-auto mb-6">
              Segments group contacts by tags (e.g. VIP, customer) within an audience.
            </p>
            <Button onClick={openCreate} className="rounded-sm btn-brand font-semibold">
              <Plus className="h-4 w-4 mr-2" /> New segment
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {segments.map((s) => (
              <div key={s.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`segment-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-display font-bold text-xl tracking-tight">{s.name}</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                      <Link to={`/audiences/${s.audience_id}`} className="underline underline-offset-2 hover:text-neutral-900 dark:hover:text-neutral-50">
                        {s.audience_name}
                      </Link>
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => openEdit(s)} className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => remove(s.id)} className="p-2 text-neutral-400 hover:text-red-600" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {s.description && <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">{s.description}</p>}
                <div className="flex flex-wrap gap-2 mb-4">
                  {(s.tags || []).map((t) => (
                    <span key={t} className="text-xs font-semibold px-2 py-1 rounded-sm bg-neutral-100 text-neutral-700 dark:text-neutral-300">{t}</span>
                  ))}
                  {!s.tags?.length && <span className="text-xs text-neutral-400">All subscribed contacts</span>}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500 dark:text-neutral-400">
                    Match: <strong>{s.match_mode === "all" ? "all tags" : "any tag"}</strong>
                  </span>
                  <span className="flex items-center gap-1 font-semibold">
                    <Users className="h-4 w-4" /> {s.contact_count} contacts
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && total > 0 && (
          <ListPagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
            className="mt-4 rounded-sm border border-neutral-200 dark:border-neutral-800"
          />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit segment" : "New segment"}</DialogTitle>
          </DialogHeader>
          <DialogScrollBody>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" placeholder="VIP customers" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Audience</Label>
                <Select value={form.audience_id} onValueChange={(v) => setForm({ ...form, audience_id: v })}>
                  <SelectTrigger className="mt-2 rounded-sm"><SelectValue placeholder="Select audience" /></SelectTrigger>
                  <SelectContent>
                    {audiences.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-2 rounded-sm" placeholder="vip, customer" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Match mode</Label>
                <Select value={form.match_mode} onValueChange={(v) => setForm({ ...form, match_mode: v })}>
                  <SelectTrigger className="mt-2 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any of these tags</SelectItem>
                    <SelectItem value="all">All of these tags</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Description (optional)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-sm" />
              </div>
            </div>
          </DialogScrollBody>
          <DialogFooter className="shrink-0 border-t border-neutral-200 dark:border-neutral-800 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="rounded-sm btn-brand font-semibold">{editingId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
